
import { styled } from '@mui/material/styles';
import { AppBar, Stack, Box, Button, IconButton } from '@mui/material';
import { styleTokens } from '../assets/styles/commonStyles';

export const StyledAppBar = styled(AppBar)(({ theme }) => ({
  // Brand: Midnight header
  backgroundColor: theme.palette.primary.main, // primary Midnight header color
  height: '5rem',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.25)', // neutral shadow on dark header
  backdropFilter: 'blur(10px)',
  // ultra subtle divider; avoid off-brand blue in borders
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
}));

export const StyledHeaderStack = styled(Stack)({
  height: '100%',
});

export const StyledLogoContainer = styled(Box)(({ theme }) => ({
  minWidth: 'var(--sidebar-width, calc(100vw * 2 / 12))',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  paddingLeft: '26px',
  paddingRight: '8px',
  overflow: 'hidden',
  flexShrink: 0,
  height: '100%',
  transition: 'width 0.1s ease-out',

  [theme.breakpoints.down('md')]: {
    paddingLeft: '16px',
    paddingRight: '4px',
  },

  [theme.breakpoints.down('sm')]: {
    paddingLeft: '12px',
    paddingRight: '2px',
  },
}));

export const StyledLogo = styled('div')(({ theme }) => ({
  maxWidth: 'calc(100% - 8px)',
  height: '60px',
  backgroundSize: 'contain',
  backgroundPosition: 'left center',
  cursor: 'pointer',
  backgroundRepeat: 'no-repeat',
  minWidth: '120px',
  transition: 'all 0.2s ease-in-out',
  transform: 'scale(1.1)',
  
  '&:hover': {
    transform: 'scale(1.1)'
  },

  [theme.breakpoints.down('lg')]: {
    minWidth: '110px'
  },

  [theme.breakpoints.down('md')]: {
    minWidth: '96px',
    height: '48px'
  },

  [theme.breakpoints.down('sm')]: {
    minWidth: '72px',
    height: '38px'
  }
}));

export const StyledContentStack = styled(Stack)({
  flexGrow: 1,
  padding: '.5rem 1rem',
});

// Header primary button (on Midnight background)
export const StyledHeaderButton = styled(Button)(({ theme }) => ({
  color: theme.palette.common.white,
  borderRadius: theme.spacing(2),
  fontSize: '0.95rem',
  fontWeight: 500,
  transition: styleTokens.transitions.default,
  backgroundColor: theme.palette.primary.main,

  '&:hover': {
    // Cyan hover tint (brand rule) with strong contrast text
    backgroundColor: 'rgba(0, 191, 232, 0.20)', // Cyan tint ~20%
    borderColor: 'rgba(0, 191, 232, 0.35)',
    color: theme.palette.common.white,
  },

  '&:active': {
    backgroundColor: 'rgba(4, 93, 139, 0.28)', // Venice tint for pressed
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.info.main}`, // Cyan focus ring
    outlineOffset: '2px',
  },

  '&:disabled': {
    color: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1),
    fontSize: '0.65rem',
  },

  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0.5, 1.25),
  },
}));

export const StyledHeaderContainer = styled('div')({
  '&': {
    '--sidebar-width': 'calc(100vw * 2 / 12)',
  },
});

export const StyledActionButtonsStack = styled(Stack)({
  marginLeft: '1rem',
});

export const StyledExitButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: 'rgba(255, 255, 255, 0.10)',
  borderRadius: theme.spacing(2),
  transition: styleTokens.transitions.default,

  '&:hover': {
    backgroundColor: 'rgba(0, 191, 232, 0.20)', // Cyan hover tint
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.info.main}`, // Cyan focus ring
    outlineOffset: '2px',
  },

  '& .MuiSvgIcon-root': {
    width: '0.85em',
    height: '0.85em',
  },

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
  },

  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0.75),
  },
}));

export const StyledButtonStack = styled(Stack)(({ theme }) => ({
  backgroundColor: 'transparent',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(0.5, 1),
  transition: styleTokens.transitions.default,

  '& .MuiButton-root': {
    color: theme.palette.common.white,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: theme.spacing(1.5),
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'none',
    transition: styleTokens.transitions.default,
    border: '1px solid rgba(255, 255, 255, 0.15)',

    '&:hover': {
      backgroundColor: 'rgba(0, 191, 232, 0.20)', // Cyan hover
      borderColor: 'rgba(0, 191, 232, 0.35)',
      transform: 'scale(1.02)',
    },

    '&:active': {
      backgroundColor: 'rgba(4, 93, 139, 0.28)', // Venice pressed
      borderColor: 'rgba(4, 93, 139, 0.45)',
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.palette.info.main}`,
      outlineOffset: '2px',
    },

    '&:disabled, &.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.5)',
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      cursor: 'not-allowed',
      transform: 'none',

      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        transform: 'none',
      },
    },

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5, 1),
      fontSize: '0.65rem',
    },

    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0.5, 1.25),
    },
  },

  '& .MuiFormControl-root': {
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.8)',
      fontSize: '0.875rem',

      '&.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.4)',
      },
    },

    '& .MuiOutlinedInput-root': {
      color: theme.palette.common.white,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      borderRadius: theme.spacing(1.5),
      border: '1px solid rgba(255, 255, 255, 0.15)',

      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },

      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        borderColor: 'rgba(0, 191, 232, 0.35)', // Cyan hover border
      },

      '&.Mui-focused': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        borderColor: theme.palette.info.main, // Cyan focus
        boxShadow: `0 0 0 2px ${theme.palette.info.light}`, // Cyan-5 halo
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
      },

      '&.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.4)',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        cursor: 'not-allowed',

        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },

    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.8)',
      transition: styleTokens.transitions.default,

      '&.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.3)',
      },
    },
  },
}));